import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router';

import { useSession } from '@/entities/session';
import { ErrorMessage } from '@/shared/ui';
import { CookieBanner } from '@/widgets/cookie-banner';
import { Header } from '@/widgets/header';
import { AppNav } from '@/widgets/nav';

import styles from './Layout.module.css';

/**
 * Page frame. Follows the signed-in user's language, so that the interface
 * speaks the same language on every device they use.
 */
export function Layout(): React.JSX.Element {
  const { i18n } = useTranslation();
  const session = useSession();
  const locale = session.data?.locale;

  useEffect(() => {
    if (locale && locale !== i18n.resolvedLanguage) {
      void i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage ?? 'ru';
  }, [i18n.resolvedLanguage]);

  return (
    <div className={[styles.shell, session.data ? styles.withNav : ''].join(' ')}>
      <Header nav={<AppNav />} />
      <main>
        <Outlet />
      </main>
      <CookieBanner />
    </div>
  );
}

/** Renders the nested routes only for a signed-in user. */
export function RequireSession(): React.JSX.Element | null {
  const session = useSession();

  if (session.isPending) {
    return null;
  }

  if (session.isError) {
    return <ErrorMessage error={session.error} />;
  }

  return session.data ? <Outlet /> : <Navigate to="/login" replace />;
}

/**
 * The root page: today for a signed-in user, the landing page for a visitor,
 * so that the address of the site explains it instead of asking for a login.
 */
export function Home({
  signedIn,
  guest,
}: {
  signedIn: React.ReactNode;
  guest: React.ReactNode;
}): React.JSX.Element | null {
  const session = useSession();

  if (session.isPending) {
    return null;
  }

  return <>{session.data ? signedIn : guest}</>;
}

/** Renders the nested routes only when nobody is signed in. */
export function RequireGuest(): React.JSX.Element | null {
  const session = useSession();

  if (session.isPending) {
    return null;
  }

  return session.data ? <Navigate to="/" replace /> : <Outlet />;
}
